import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError } from '@/lib/api/client';
import { loadBranding } from '@/lib/branding';
import { logError } from '@/lib/log';
import { App } from './App';
import './styles/app.css';

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: (e, q) => logError('app', `query ${JSON.stringify(q.queryKey)} failed`, e) }),
  mutationCache: new MutationCache({ onError: (e) => logError('app', 'mutation failed', e) }),
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      // Client errors (4xx) won't fix themselves; retry network and server errors twice.
      retry: (count, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && count < 2,
    },
  },
});

async function boot() {
  if (import.meta.env.MODE === 'mock') {
    const { startMockWorker } = await import('./mocks/browser');
    await startMockWorker();
  }
  // The host's brand (white-label) before the first paint, so a tenant's users never see another logo.
  await loadBranding();
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}

boot().catch((e) => logError('app', 'failed to start', e));
